import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, mainSiteUrl, faqs } from '@/mocks/categories';
import { useState } from 'react';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://acbuyspreadsheet.one';

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "AcBuy Spreadsheet Guide 2026: How to Find Trusted Products & Sellers",
  "description": "Complete guide to the AcBuy Spreadsheet. Learn how it works, find trusted sellers, compare products, and shop safely with expert tips.",
  "image": "https://readdy.ai/api/search-image?query=Modern%20minimalist%20organizer%20workspace%20with%20laptop%20displaying%20product%20database%20and%20organized%20data%2C%20warm%20cream%20background%2C%20clean%20aesthetic%2C%20soft%20natural%20lighting%2C%20editorial%20photography%2C%20professional%20atmosphere%2C%20neat%20desk%20with%20stationery&width=1200&height=500&seq=article-whatis-v3&orientation=landscape",
  "datePublished": "2026-06-01",
  "dateModified": "2026-06-25",
  "author": { "@type": "Organization", "name": "AcBuy Community" },
  "publisher": { "@type": "Organization", "name": "AcBuy Spreadsheet", "url": siteUrl },
  "mainEntityOfPage": siteUrl + "/acbuy-spreadsheet-guide"
};

export default function ArticlePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main className="pt-20 pb-10">
        <article className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground-800 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800">AcBuy Spreadsheet Complete Guide</span>
            </nav>

            {/* H1 */}
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground-950 leading-tight mb-4">
              AcBuy Spreadsheet Guide 2026: How to Find Trusted Products &amp; Sellers
            </h1>

            {/* Meta info */}
            <div className="flex items-center gap-4 text-xs text-foreground-500 mb-8 flex-wrap">
              <span>Updated: June 2026</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>12 min read</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>By AcBuy Community</span>
            </div>

            {/* Quick Answer - Featured Snippet */}
            <div className="bg-accent-50 border border-accent-200 rounded-xl p-5 md:p-6 mb-10">
              <p className="text-xs font-semibold text-accent-700 uppercase tracking-wider mb-2">Quick Answer</p>
              <p className="text-base md:text-lg font-semibold text-foreground-950 leading-relaxed">
                <strong>AcBuy Spreadsheet</strong> is a curated database that helps users discover products, trusted sellers, and shopping information. It organizes thousands of items across 14 categories — including shoes, hoodies, jackets, and accessories — making it the fastest way to find verified product links without manually searching through multiple platforms.
              </p>
            </div>

            {/* Table of Contents */}
            <nav className="bg-background-100 rounded-xl p-5 md:p-6 mb-10" aria-label="Table of Contents">
              <h2 className="text-sm font-bold text-foreground-950 mb-4">Table of Contents</h2>
              <ol className="space-y-2">
                {[
                  'What Is AcBuy Spreadsheet?',
                  'How AcBuy Spreadsheet Works',
                  'Why Use AcBuy Spreadsheet? (Pros & Cons)',
                  'AcBuy Spreadsheet vs Manual Search: Full Comparison',
                  'Product Categories in the Spreadsheet',
                  'How to Find the Best AcBuy Spreadsheet in 2026',
                  'Is AcBuy Spreadsheet Safe? Complete Safety Guide',
                  'Frequently Asked Questions About AcBuy Spreadsheet',
                  'Final Verdict: Should You Use AcBuy Spreadsheet?',
                ].map((item, idx) => (
                  <li key={idx} className="text-sm">
                    <a href={`#section-${idx + 1}`} className="text-foreground-600 hover:text-primary-600 transition-colors">
                      {idx + 1}. {item}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* Section 1: What Is */}
            <section id="section-1" className="mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">1. What Is AcBuy Spreadsheet?</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                The <strong>AcBuy Spreadsheet</strong> is a comprehensive, community-maintained database that collects and organizes product links, seller information, and buying resources into a single, easy-to-navigate spreadsheet. Instead of jumping between multiple websites, apps, and forums to find what you need, the AcBuy spreadsheet centralizes everything so you can browse, compare, and shop efficiently.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Originally created by experienced community members who were tired of the fragmented shopping experience, the <strong>best AcBuy spreadsheet</strong> has grown to include over <strong>10,000 products</strong> across <strong>14 distinct categories</strong>. From sneakers and streetwear to luxury accessories and everyday essentials, the spreadsheet covers virtually every shopping need.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                What sets the <strong>AcBuy spreadsheet 2026</strong> apart from a simple product list is its community-driven verification system. Every seller link included in the spreadsheet has been reviewed and vetted by actual users, significantly reducing the risk of encountering scams, fake products, or unreliable sellers. This makes it one of the most <strong>trusted AcBuy spreadsheet</strong> resources available today.
              </p>
              <div className="rounded-xl overflow-hidden my-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20organizer%20workspace%20with%20laptop%20displaying%20product%20database%20and%20organized%20data%2C%20warm%20cream%20background%2C%20clean%20aesthetic%2C%20soft%20natural%20lighting%2C%20editorial%20photography%2C%20professional%20atmosphere%2C%20neat%20desk%20with%20stationery&width=1200&height=500&seq=article-whatis-v3&orientation=landscape"
                  alt="AcBuy Spreadsheet explained - organized product database on laptop"
                  className="w-full h-56 md:h-72 object-cover object-top"
                />
              </div>
            </section>

            {/* Section 2: How It Works */}
            <section id="section-2" className="mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">2. How AcBuy Spreadsheet Works</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Using the <strong>AcBuy spreadsheet</strong> is straightforward. The spreadsheet is structured with clear columns and categories, allowing you to navigate quickly to the section that interests you. Here is a step-by-step breakdown of how it works:
              </p>
              <div className="space-y-4 mb-4">
                {[
                  { step: '01', title: 'Access the Spreadsheet', desc: 'Visit the main AcBuy website to open the latest version of the spreadsheet. The file is hosted online and accessible from any device.' },
                  { step: '02', title: 'Browse by Category', desc: 'Navigate through 14 organized categories including Shoes, Hoodies, T-Shirts, Jackets, Bags, and more. Each category contains verified product links.' },
                  { step: '03', title: 'Review Product Details', desc: 'Click on any product link to view detailed information including pricing, seller ratings, available sizes, and community feedback.' },
                  { step: '04', title: 'Verify the Seller', desc: 'Check the seller verification status and community reviews before making a purchase. The spreadsheet includes notes on seller reliability.' },
                  { step: '05', title: 'Make Your Purchase', desc: 'Once you have found a trusted seller with the product you want, follow the link to complete your purchase confidently.' },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4 p-4 bg-background-100 rounded-xl">
                    <span className="text-2xl font-extrabold text-primary-500 flex-shrink-0">{item.step}</span>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-950 mb-1">{item.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 3: Pros & Cons */}
            <section id="section-3" className="mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">3. Why Use AcBuy Spreadsheet? (Pros &amp; Cons)</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                <div className="bg-accent-50 border border-accent-200 rounded-xl p-5">
                  <h3 className="text-sm font-bold text-accent-800 mb-3 flex items-center gap-2">
                    <i className="ri-check-line"></i> Pros
                  </h3>
                  <ul className="space-y-2">
                    {[
                      'Saves hours of manual searching across platforms',
                      'Community-verified seller links reduce scam risk',
                      '14 organized categories for easy navigation',
                      'Updated daily with new products and links',
                      'Completely free to access and use',
                      'Works on any device — desktop or mobile',
                      'Includes buying guides and safety tips',
                    ].map((pro, i) => (
                      <li key={i} className="text-sm text-foreground-700 flex items-start gap-2">
                        <i className="ri-check-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                        {pro}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-5">
                  <h3 className="text-sm font-bold text-secondary-800 mb-3 flex items-center gap-2">
                    <i className="ri-close-line"></i> Cons
                  </h3>
                  <ul className="space-y-2">
                    {[
                      'Requires internet access to view the spreadsheet',
                      'Not all products may be available in every region',
                      'Users should still verify sellers individually',
                      'New users may need time to learn navigation',
                      'Some categories have more products than others',
                    ].map((con, i) => (
                      <li key={i} className="text-sm text-foreground-700 flex items-start gap-2">
                        <i className="ri-close-line text-secondary-600 mt-0.5 flex-shrink-0"></i>
                        {con}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 4: Comparison */}
            <section id="section-4" className="mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">4. AcBuy Spreadsheet vs Manual Search: Full Comparison</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Why do thousands of users choose the <strong>AcBuy spreadsheet</strong> over manually searching? The table below breaks down the key differences and shows you exactly how much time and effort you save by using this organized resource.
              </p>
              <div className="bg-background-100 rounded-xl overflow-hidden border border-background-200/60 mb-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-background-200/60">
                        <th className="text-left px-5 py-3 text-sm font-semibold text-foreground-950">Feature</th>
                        <th className="text-center px-5 py-3 text-sm font-semibold text-primary-600">AcBuy Spreadsheet</th>
                        <th className="text-center px-5 py-3 text-sm font-semibold text-foreground-500">Manual Search</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Time to Find Product', 'Under 1 minute', '10-30 minutes'],
                        ['Seller Verification', 'Community-vetted', 'Self-research required'],
                        ['Number of Categories', '14 organized', 'Scattered across sites'],
                        ['Price Comparison', 'Side by side', 'Manual only'],
                        ['Update Frequency', 'Daily', 'Depends on you'],
                        ['Scam Protection', 'Built-in checks', 'Your own risk'],
                        ['Buying Guidance', 'Included guides', 'None included'],
                        ['Mobile Access', 'Full support', 'Varies by platform'],
                      ].map((row, idx) => (
                        <tr key={idx} className={`border-b border-background-200/40 ${idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}`}>
                          <td className="px-5 py-3 text-sm font-medium text-foreground-800">{row[0]}</td>
                          <td className="px-5 py-3 text-center text-sm font-medium text-accent-700">{row[1]}</td>
                          <td className="px-5 py-3 text-center text-sm text-foreground-500">{row[2]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">
                As the comparison clearly shows, the <strong>best AcBuy spreadsheet</strong> saves significant time and provides built-in protections that manual searching simply cannot match. For anyone shopping regularly, the spreadsheet is an essential tool that pays for itself in time saved alone.
              </p>
            </section>

            {/* Section 5: Categories */}
            <section id="section-5" className="mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">5. Product Categories in the Spreadsheet</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                The <strong>AcBuy spreadsheet 2026</strong> organizes products into <strong>14 well-defined categories</strong>. Each category is maintained separately and updated with fresh links on a daily basis. Here is the complete breakdown:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 p-3.5 bg-background-100 hover:bg-background-200 rounded-xl transition-colors cursor-pointer group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className={`${cat.icon} text-sm text-primary-600`}></i>
                    </div>
                    <div className="min-w-0">
                      <span className="text-sm font-semibold text-foreground-800 group-hover:text-foreground-950">{cat.name}</span>
                      <p className="text-xs text-foreground-500 truncate">{cat.description}</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-foreground-700 ml-auto flex-shrink-0"></i>
                  </a>
                ))}
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Each category in the <strong>AcBuy spreadsheet</strong> is regularly reviewed and updated. Popular categories like Shoes and Hoodies tend to have the largest selection, while niche categories like Belts and Perfumes offer more curated collections of high-quality finds.
              </p>
            </section>

            {/* Section 6: How to Find the Best */}
            <section id="section-6" className="mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">6. How to Find the Best AcBuy Spreadsheet in 2026</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                With the growing popularity of the <strong>AcBuy spreadsheet</strong>, it is important to know what to look for when choosing which version or source to use. Not all spreadsheets are created equal, and using the right one can make a significant difference in your shopping experience.
              </p>
              <div className="space-y-3 mb-4">
                {[
                  { title: 'Look for Daily Updates', desc: 'The best AcBuy spreadsheet is updated every day. Stale links and outdated seller information can lead to frustrating dead ends. Always check when the spreadsheet was last refreshed before relying on its contents.' },
                  { title: 'Check for Community Verification', desc: 'A quality spreadsheet will have clear indicators showing which sellers have been vetted by the community. Look for verification badges, review counts, and seller ratings within the spreadsheet.' },
                  { title: 'Verify Category Coverage', desc: 'The spreadsheet should cover all 14 standard categories. If major categories like Shoes or Hoodies are missing, the spreadsheet may be incomplete or poorly maintained.' },
                  { title: 'Test a Few Links', desc: 'Before committing to a particular spreadsheet, click through a handful of links across different categories. This quick check confirms that the links are active and lead to legitimate product pages.' },
                ].map((tip, idx) => (
                  <div key={idx} className="flex gap-3 p-4 bg-background-100 rounded-xl">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <span className="text-xs font-bold text-primary-600">{idx + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-950 mb-1">{tip.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{tip.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 7: Safety */}
            <section id="section-7" className="mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">7. Is AcBuy Spreadsheet Safe? Complete Safety Guide</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Safety is the number one concern for anyone using the <strong>AcBuy spreadsheet</strong>. The good news is that when used correctly, the spreadsheet is a significantly safer way to shop compared to randomly browsing unverified listings across the internet.
              </p>
              <div className="bg-foreground-950 rounded-2xl p-6 md:p-8 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-500/20">
                    <i className="ri-shield-check-line text-accent-400 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-background-50">Community Safety Features</h3>
                    <p className="text-xs text-background-50/60">Built-in protections in the AcBuy spreadsheet</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {[
                    'Every seller link is reviewed by community members before inclusion',
                    'Suspicious or broken links are flagged and removed within 24 hours',
                    'Seller ratings and review counts are displayed directly in the spreadsheet',
                    'The spreadsheet itself contains no executable code or tracking scripts',
                    'You always complete purchases on the original platform, not through the spreadsheet',
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-background-50/80">
                      <i className="ri-check-line text-accent-400 mt-0.5 flex-shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                While the <strong>AcBuy spreadsheet</strong> provides strong safety features, we always recommend following these additional precautions:
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  'Always inspect QC (quality control) photos before finalizing any purchase',
                  'Check the seller rating and number of completed transactions before buying',
                  'Use secure payment methods with buyer protection whenever possible',
                  'Be cautious of deals that seem too good to be true',
                  'Keep records of your transactions and communications with sellers',
                ].map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-shield-star-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                    {tip}
                  </li>
                ))}
              </ul>
            </section>

            {/* Section 8: FAQ */}
            <section id="section-8" className="mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">8. Frequently Asked Questions About AcBuy Spreadsheet</h2>
              <div className="space-y-3">
                {faqs.map((faq, idx) => (
                  <div key={idx} className="bg-background-100 rounded-xl overflow-hidden border border-background-200/60">
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="text-sm font-semibold text-foreground-950 pr-4">{faq.question}</span>
                      <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 flex-shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openFaq === idx && (
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Section 9: Final Verdict */}
            <section id="section-9" className="mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">9. Final Verdict: Should You Use AcBuy Spreadsheet?</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                After reviewing every aspect of the <strong>AcBuy spreadsheet</strong> — from its category organization and seller verification system to its daily update schedule and safety features — the answer is clear. For anyone who shops regularly across multiple categories, the <strong>best AcBuy spreadsheet 2026</strong> is an invaluable resource that saves time, reduces risk, and improves the overall shopping experience.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                The combination of <strong>10,000+ products</strong>, <strong>500+ verified sellers</strong>, and <strong>daily updates</strong> makes this spreadsheet the most comprehensive and reliable shopping tool available. Whether you are looking for the latest sneakers, a warm hoodie, designer accessories, or everyday essentials, the spreadsheet has you covered.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                We give the <strong>AcBuy spreadsheet</strong> our strongest recommendation. It is free, easy to use, and continuously improving thanks to an active community of contributors. Start browsing today and experience the difference that organized, verified shopping makes.
              </p>

              <div className="bg-primary-50 border border-primary-200 rounded-2xl p-6 md:p-8 text-center">
                <h3 className="text-lg font-bold text-foreground-950 mb-3">Ready to Start Shopping Smarter?</h3>
                <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto leading-relaxed">
                  Access the complete AcBuy Spreadsheet now and discover thousands of verified products across all 14 categories.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold px-8 py-3.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer"
                  >
                    Browse AcBuy Spreadsheet
                    <i className="ri-arrow-right-line"></i>
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Back to Home
                  </Link>
                </div>
              </div>
            </section>

            {/* Related links */}
            <div className="border-t border-background-200/60 pt-8">
              <h3 className="text-sm font-bold text-foreground-950 mb-4">Explore More</h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/" className="text-sm text-foreground-600 hover:text-primary-600 px-4 py-2 bg-background-100 rounded-full transition-colors whitespace-nowrap">Homepage</Link>
                {categories.slice(0, 5).map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-600 hover:text-primary-600 px-4 py-2 bg-background-100 rounded-full transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}